import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { useLang } from "../../i18n";

const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, i, italic }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${italic ? "italic text-[#9A4930]" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay: 0.25 + i * 0.14, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const sx = useSpring(rx, { stiffness: 80, damping: 15 });
  const sy = useSpring(ry, { stiffness: 80, damping: 15 });
  const { t } = useLang();
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 12);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 12);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <div className="[perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-tilt-frame">
      <motion.div style={{ rotateX: sx, rotateY: sy }} className="relative [transform-style:preserve-3d]">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 0 0)" }}
          transition={{ duration: 1.6, delay: 0.4, ease }}
          className="aspect-[4/5] w-full overflow-hidden bg-[#29221D]"
        >
          <motion.video
            src="/img/promo.mov"
            poster="/img/hero.jpg"
            autoPlay
            muted
            loop
            playsInline
            aria-label="Video dei capi in cashmere"
            className="w-full h-full object-cover"
            initial={{ scale: 1.25 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.2, delay: 0.4, ease }}
          />
        </motion.div>
        <div className="absolute -left-6 sm:-left-12 bottom-10 bg-[#FBF9F5] px-5 py-4 shadow-[0_30px_60px_-20px_rgba(41,34,29,0.35)] [transform:translateZ(60px)]">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#57534E]">{t("hero.comp")}</p>
          <p className="font-serif text-3xl text-[#1C1917]">100% cashmere</p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const lines = [t("hero.l1"), t("hero.l2"), t("hero.l3"), t("hero.l4")];

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] pt-28 sm:pt-32 lg:pt-40 pb-20 overflow-hidden grain">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-10 items-end">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-[#9A4930] mb-8"
          >
            {t("hero.eyebrow")}
          </motion.p>
          <h1 data-testid="hero-title" className="font-serif font-light text-[8vw] sm:text-[6.5vw] lg:text-8xl leading-[1.02] tracking-[-0.02em] text-[#1C1917]">
            {lines.map((l, i) => (
              <MaskLine key={l} i={i} italic={i === 1}>{l}</MaskLine>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease }}
            className="mt-10 max-w-lg"
          >
            <p className="text-base md:text-lg text-[#57534E] leading-relaxed">{t("hero.p")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#contatti" data-testid="hero-cta-contact" className="btn-pill bg-[#29221D] text-[#FBF9F5]">
                {t("hero.cta1")} <ArrowDownRight className="w-4 h-4" />
              </a>
              <a href="#prodotti" data-testid="hero-cta-products" className="btn-pill border border-[#29221D] text-[#29221D]">
                {t("hero.cta2")}
              </a>
            </div>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yImg }} className="lg:col-span-5 max-w-md lg:max-w-none mx-auto w-full">
          <TiltFrame />
        </motion.div>
      </div>
    </section>
  );
};
